import * as React from 'react';
import {
    Box,
    Typography,
    useTheme,
    alpha,
    Stack
} from '@mui/material';

const diasDaSemanaLabels = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const mesesLabels = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export default function CalendarioDiaPanel(props) {
    const { data, eventos, setShowEvento, setIdEvento, feriado } = props;
    const theme = useTheme();

    const feriadoDoDia = React.useMemo(() => {
        if (!feriado || !feriado.lst) return null;

        const targetDateStr = `${String(data.getDate()).padStart(2, '0')}-${String(data.getMonth() + 1).padStart(2, '0')}-${data.getFullYear()}`;

        return feriado.lst.find(e => {
            const [ano, mes, dia] = e.date.split('-');
            return `${dia}-${mes}-${ano}` === targetDateStr;
        });
    }, [data, feriado]);

    const gradeHoras = React.useMemo(() => {
        if (!eventos) return [];

        const horas = [];
        for (let h = 0; h < 24; h++) {
            const dtInicioHora = new Date(data);
            dtInicioHora.setHours(h, 0, 0, 0);

            const dtFimHora = new Date(data);
            dtFimHora.setHours(h, 59, 59, 999);

            const eventosDaHora = eventos.filter(e => {
                const evtStart = new Date(e.dtInicial);
                let evtEnd = e.dtFinal ? new Date(e.dtFinal) : new Date(e.dtInicial);
                evtEnd.setHours(23, 59, 59, 0);

                return evtStart <= dtFimHora && evtEnd >= dtInicioHora && evtStart.getHours() === h;
            });

            horas.push({
                horaNum: h,
                label: `${h.toString().padStart(2, '0')}:00`,
                eventos: eventosDaHora
            });
        }
        return horas;
    }, [data, eventos]);

    const gridTemplate = '80px 1fr';

    const isHoje = React.useMemo(() => {
        const hoje = new Date();
        return data.getDate() === hoje.getDate() &&
            data.getMonth() === hoje.getMonth() &&
            data.getFullYear() === hoje.getFullYear();
    }, [data]);

    return (
        <Box
            sx={{
                width: '100%',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: '600px',
                bgcolor: 'background.paper',
                overflowY: 'auto',
                position: 'relative'
            }}
        >
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.grey[50],
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ borderRight: `1px solid ${theme.palette.divider}` }} />

                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: feriadoDoDia ? alpha(theme.palette.error.light, 0.1) : (isHoje ? alpha(theme.palette.primary.main, 0.05) : 'transparent')
                    }}
                >
                    <Typography variant="h6" color={isHoje ? "primary.main" : "text.primary"} sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {data.getDate()} de {mesesLabels[data.getMonth()]}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            {diasDaSemanaLabels[data.getDay()]}
                        </Typography>
                        {feriadoDoDia && (
                            <Typography variant="caption" sx={{ bgcolor: theme.palette.error.main, color: 'white', px: 1, py: 0.2, borderRadius: 1, fontWeight: 'bold' }}>
                                {feriadoDoDia.name}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box>
                {gradeHoras.map((linha) => (
                    <Box
                        key={`hora-${linha.horaNum}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: gridTemplate,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            minHeight: '80px',
                            '&:last-child': { borderBottom: 'none' }
                        }}
                    >
                        <Box
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                borderRight: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.grey[50]
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" fontWeight="500">
                                {linha.label}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {linha.eventos.map((evt) => {
                                const dt = new Date(evt.dtInicial);
                                const horaStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                return (
                                    <Box
                                        key={evt.id}
                                        onClick={() => {
                                            setIdEvento(evt.id);
                                            setShowEvento(true);
                                        }}
                                        sx={{
                                            bgcolor: theme.palette.primary.light,
                                            color: theme.palette.primary.contrastText,
                                            px: 2,
                                            py: 1,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            boxShadow: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 3
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', bgcolor: alpha('#fff', 0.2), px: 0.8, py: 0.2, borderRadius: 1 }}>
                                            {horaStr}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {evt.nomeCurto}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}